import React, {useState, useEffect} from 'react';
import '../../styles/Dashboard.css'
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Card from "../../components/Card";
import Astuce from "../../components/Astuce";

function BotDashboard({dark, setDark, sidebarOpen, setSidebarOpen}) {
    const toggleTheme = () => setDark(!dark);

    // Bot status (fetched from backend)
    const [uptimeSeconds, setUptimeSeconds] = useState(null);
    const [uptimeText, setUptimeText] = useState('—');
    const [statusFetchError, setStatusFetchError] = useState(null);
    const [requestsTotal, setRequestsTotal] = useState(null);
    const [guildCount, setGuildCount] = useState(null);
    const [membersTotal, setMembersTotal] = useState(null);
    const [guildMembersEvents, setGuildMembersEvents] = useState([]);
    const [requestsEvents, setRequestsEvents] = useState([]);
    const [requestsChange] = useState('Stable');
    const [gardenSemisCount] = useState(12);
    const [gardenNotesCount] = useState(3);
    const [recipesCount] = useState(42);

    // small helper reused from Dashboard to format dates
    const formatDate = (d, includeYear = false) => {
        if (!d) return "—";
        try {
            const opts = { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' };
            if (includeYear) opts.year = 'numeric';
            return new Date(d).toLocaleString("fr-FR", opts);
        } catch {
            return "—";
        }
    };

    const truncateText = (text, max = 60) => {
        if (!text) return "—";
        if (typeof text !== 'string') text = String(text);
        if (text.length <= max) return text;
        return text.slice(0, max - 1) + "…";
    };

    const statusClass = (s) => {
        if (!s) return "status-info";
        if (s === "error") return "status-cancelled";
        if (s === "warning") return "status-pending";
        if (s === "new") return "status-success";
        if (s === "torrent" || s === "torrent-requested" || s === "Request" || s === "Semis") return "status-success";
        if (s === "cancelled" || s === "Annulé" ) return "status-cancelled";
        return "status-info";
    };

    const presenceClass = (p) => {
        if (!p) return 'presence-offline';
        const key = String(p).toLowerCase();
        if (key === 'online') return 'presence-online';
        if (key === 'idle') return 'presence-idle';
        if (key === 'dnd' || key === 'busy') return 'presence-dnd';
        return 'presence-offline';
    };

    const prettyPresence = (p) => {
        if (!p) return 'Hors ligne';
        const key = String(p).toLowerCase();
        if (key === 'online') return 'En ligne';
        if (key === 'idle') return 'Absent';
        if (key === 'dnd' || key === 'busy') return "Ne pas déranger";
        return 'Hors ligne';
    };

    const prettyRequestStatus = (s) => {
        if (!s) return '—';
        const key = String(s).toLowerCase();
        if (key === 'new' || key === 'created') return 'Nouveau';
        if (key === 'pending' || key === 'waiting') return 'En attente';
        if (key === 'done' || key === 'completed' || key === 'ok') return 'Terminé';
        if (key === 'cancelled' || key === 'annulé' || key === 'canceled') return 'Annulé';
        return String(s);
    };

    // map some specific actor IDs to friendly names
    const translateActor = (a) => {
        if (!a && a !== 0) return a;
        const key = String(a);
        if (key === '723421020471427122') return 'Strass';
        if (key === '195249015858790400') return 'Phil';
        if (key === '3') return 'Phil et Strass';
        return a;
    };

    // Fetch bot status from backend on mount
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await fetch('https://api.industriegaming.fr/api/bot/status/fetch');
                if (!res.ok) {
                    setStatusFetchError(`Fetch failed ${res.status}`);
                    return;
                }
                const body = await res.json();
                // body.data should contain the remote status
                const uptime = body && body.data && typeof body.data.uptime !== 'undefined' ? Number(body.data.uptime) : null;
                if (uptime !== null && !Number.isNaN(uptime)) {
                    setUptimeSeconds(uptime);
                    // format uptime nicely with days when relevant
                    if (uptime >= 86400) { // days
                        const days = Math.floor(uptime / 86400);
                        const hours = Math.floor((uptime % 86400) / 3600);
                        setUptimeText(`${days}d ${hours}h`);
                    } else if (uptime >= 3600) {
                        const hours = Math.floor(uptime / 3600);
                        const minutes = Math.floor((uptime % 3600) / 60);
                        setUptimeText(`${hours}h ${minutes}m`);
                    } else if (uptime >= 60) {
                        const m = Math.floor(uptime / 60);
                        const s = Math.floor(uptime % 60);
                        setUptimeText(`${m}m ${s}s`);
                    } else {
                        setUptimeText(`${uptime.toFixed(1)} s`);
                    }
                } else {
                    setStatusFetchError('No uptime in response');
                }
                // also extract requests.total if present
                const reqTotal = body && body.data && body.data.requests && typeof body.data.requests.total !== 'undefined'
                    ? Number(body.data.requests.total) : null;
                if (reqTotal !== null && !Number.isNaN(reqTotal)) {
                    setRequestsTotal(reqTotal);
                }
                // try to extract the recent request items array from the response (defensive)
                const reqObj = body && body.data && body.data.requests ? body.data.requests : null;
                const extractRequestItems = (r) => {
                    if (!r) return [];
                    // Prefer explicit 'recent' fields used by some APIs
                    if (Array.isArray(r.recent)) return r.recent;
                    if (Array.isArray(r.recentRequests)) return r.recentRequests;
                    if (Array.isArray(r.recent_items)) return r.recent_items;
                    if (Array.isArray(r.items)) return r.items;
                    if (Array.isArray(r.list)) return r.list;
                    if (Array.isArray(r.history)) return r.history;
                    if (Array.isArray(r.requests)) return r.requests;
                    if (Array.isArray(r)) return r;
                    return [];
                };

                const rawRequests = extractRequestItems(reqObj);
                if (rawRequests.length) {
                    const mapped = rawRequests.map((rq, idx) => ({
                        id: rq.id || rq._id || rq.requestId || `req-${idx}`,
                        actor: translateActor(rq.user || rq.username || rq.requester || rq.actor || (rq.meta && rq.meta.user) || 'Utilisateur'),
                        // followmaj: whether the request is followed for updates (try a few common keys)
                        followmaj: Boolean(rq.followmaj || rq.follow_up || rq.followUp || rq.follow || rq.followed || rq.following || false),
                        detail: rq.title || rq.detail || rq.query || rq.name || rq.command || JSON.stringify(rq).slice(0, 120),
                        avatar: rq.avatar || (rq.user && rq.user.avatar) || null,
                        status: rq.status || rq.state || rq.type || rq.statusType || 'new',
                        date: rq.createdAt || rq.date || rq.ts || null
                    }));
                    setRequestsEvents(mapped);
                }
                // extract guilds count if present
                const guilds = body && body.data && body.data.guilds ? body.data.guilds : null;
                if (Array.isArray(guilds)) {
                    setGuildCount(guilds.length);
                    const sum = guilds.reduce((acc, g) => acc + (Number(g.memberCount) || 0), 0);
                    setMembersTotal(sum);

                    // build member events for the activity table
                    const memberEvents = [];
                    guilds.forEach(g => {
                        const guildName = g.name || `Guild ${g.id}`;
                        (g.members || []).forEach(m => {
                            memberEvents.push({
                                id: `member-${m.id}`,
                                actor: translateActor(m.displayName || m.username || m.id),
                                server: guildName,
                                detail: m.id,
                                avatar: m.avatar || null,
                                status: m.presence || 'offline'
                            });
                        });
                    });

                    setGuildMembersEvents(memberEvents);
                }
            } catch (err) {
                console.error('Error fetching bot status from backend:', err);
                setStatusFetchError(err.message || String(err));
            }
        };

        fetchStatus();
    }, []);

    // Fake static data (no API calls)
    const [torrentsChange] = useState('↑ 8 % vs mois précédent');
    const mockEvents = [
        { id: '1', actor: 'Bot', action: 'Commande reçue', detail: '!t search ubuntu 20.04', status: 'Request', statusType: 'Request', date: new Date().toISOString() },
        { id: '2', actor: 'Discord', action: 'Torrent demandé', detail: 'Ubuntu.ISO', status: 'Demandé', statusType: 'torrent-requested', date: new Date(Date.now() - 3600 * 1000).toISOString() },
        { id: '3', actor: 'Scheduler', action: 'Nettoyage sessions', detail: 'Sessions expirées supprimées', status: 'new', date: new Date(Date.now() - 2 * 3600 * 1000).toISOString() },
        { id: '4', actor: 'Jardin', action: 'Semis: Tomate', detail: 'Planter en intérieur', status: 'Semis', date: new Date(Date.now() - 24 * 3600 * 1000).toISOString() },
    ];

    const mockRequests = [
        { id: 'rq1', actor: 'Utilisateur1', followmaj: true, detail: '!t ubuntu 20.04', status: 'new', date: new Date().toISOString() },
        { id: 'rq2', actor: 'Utilisateur2', followmaj: false, detail: '!t debian iso', status: 'pending', date: new Date(Date.now() - 3600 * 1000).toISOString() }
    ];

    return (
        <div className={`dashboard ${dark ? "dark" : ""}`}>
            <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}/>

            <div className="main">
                <Topbar onToggleSidebar={() => setSidebarOpen(true)} onToggleTheme={toggleTheme} dark={dark} />

                <div className="content">
                    <div className="cards">
                        <Card title="Bot Uptime" value={uptimeText} sub="Durée d'activité du bot." chip={null} />
                        <Card title="Bot Requests" value={requestsTotal !== null ? requestsTotal.toLocaleString('fr-FR') : '—'} sub="Total de request (bot)." chip={requestsChange} />
                        <Card title="Bot Guilds" value={guildCount !== null ? guildCount.toLocaleString('fr-FR') : '—'} sub="Nombre de guilds (serveurs)." />
                        <Card title="Bot Members" value={membersTotal !== null ? membersTotal.toLocaleString('fr-FR') : '—'} sub="Membres totaux (toutes guilds)." />
                    </div>

                    <div className="layout-two-cols">
                        <div className="card">
                            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span className="card-title">Membres des guilds</span>
                                    </div>

                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Utilisateur</th>
                                                <th>Serveur</th>
                                                <th>Détail</th>
                                                <th>Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(guildMembersEvents.length ? guildMembersEvents : mockEvents).map(evt => (
                                                <tr key={evt.id}>
                                                    <td style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {evt.avatar ? <img src={evt.avatar} alt="avatar" style={{ width: 28, height: 28, borderRadius: 6 }} /> : null}
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <strong>{evt.actor}</strong>
                                                            {evt.server ? <small style={{ color: '#777' }}>{evt.server}</small> : null}
                                                        </div>
                                                    </td>
                                                    <td>{evt.server || '—'}</td>
                                                    <td>{evt.detail ? truncateText(evt.detail, 60) : '—'}</td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <span className={`presence-badge ${presenceClass(evt.status)}`} />
                                                            <span className={`presence-label ${presenceClass(evt.status)}`}>{prettyPresence(evt.status)}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                        </div>

                        <div className="card">
                            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="card-title">Requests</span>
                            </div>

                            <table className="table">
                                <thead>
                                    <tr>
                                            <th>Utilisateur</th>
                                            <th className="center-col">Suivi MAJ</th>
                                            <th>Détail</th>
                                            <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                        {(requestsEvents.length ? requestsEvents : mockRequests).map(evt => (
                                            <tr key={evt.id}>
                                                <td className="col-user" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {evt.avatar ? <img src={evt.avatar} alt="avatar" style={{ width: 28, height: 28, borderRadius: 6 }} /> : null}
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <strong>{evt.actor}</strong>
                                                    </div>
                                                </td>
                                                <td className="center-col">
                                                    <span className={`followmaj ${evt.followmaj ? 'true' : 'false'}`} title={evt.followmaj ? 'Suivi activé' : 'Suivi désactivé'} aria-label={evt.followmaj ? 'Suivi activé' : 'Suivi désactivé'}>
                                                        {evt.followmaj ? '✓' : '✕'}
                                                    </span>
                                                </td>
                                                <td>{evt.detail ? truncateText(evt.detail, 60) : '—'}</td>
                                                <td>{formatDate(evt.date, true)}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BotDashboard;
